import { useEffect, useRef, useState, useCallback, type FC } from "react";
import { X, ExternalLink, Loader2 } from "lucide-react";
import { useDocsDrawer } from "src/contexts/DocsDrawerContext";
import { DOCS_BASE_URL } from "src/config/docs-mapping";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

const DocsDrawer: FC = () => {
  const { t, i18n } = useTranslation("common");
  const { isOpen, docsSlug, closeDocs } = useDocsDrawer();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isNotFound, setIsNotFound] = useState(false);
  const [currentTitle, setCurrentTitle] = useState(t("drawer.defaultTitle"));
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Force re-render when language changes
  const [language, setLanguage] = useState(i18n.language);

  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      setLanguage(lng);
      // Update the current title when language changes
      setCurrentTitle(t("drawer.defaultTitle"));
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n, t]);

  const isRTL = i18n.language === 'ar';

  const iframeSrc = docsSlug
    ? `${DOCS_BASE_URL}${isRTL ? '/ar' : ''}${docsSlug}?embed=1`
    : null;

  // Listen for postMessage from docs iframe.
  //
  // The iframe sandbox does not include `allow-same-origin` (that combo with
  // `allow-scripts` is a documented sandbox-escape vector), which means
  // `event.origin` arrives as the string "null". An origin-string check is
  // therefore not viable here. We instead identify the source by Window
  // reference — only messages from the exact iframe element we own are
  // accepted. That's strictly stronger than the previous string check
  // because the Window reference cannot be spoofed by an attacker.
  const handleMessage = useCallback((event: MessageEvent) => {
    if (event.source !== iframeRef.current?.contentWindow) return;
    if (!event.data || event.data.source !== "botbat-docs") return;

    switch (event.data.type) {
      case "docs-ready":
        setIsLoading(false);
        break;
      case "route-change":
        if (event.data.payload?.title) {
          setCurrentTitle(event.data.payload.title);
        }
        setIsLoading(false);
        break;
      case "not-found":
        setIsLoading(false);
        setIsNotFound(true);
        break;
    }
  }, []);

  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [handleMessage]);

  // Reset state when slug changes
  useEffect(() => {
    if (isOpen && iframeSrc) {
      setIsLoading(true);
      setHasError(false);
      setIsNotFound(false);
      // Error timeout: if docs don't respond within 10s, show error
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setIsLoading(loading => {
          if (loading) {
            setHasError(true);
            return false;
          }
          return loading;
        });
      }, 10000);
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [iframeSrc, isOpen, language]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        closeDocs();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, closeDocs]);

  // Open full docs page in new tab
  const openInNewTab = () => {
    if (docsSlug) {
      window.open(
        `${DOCS_BASE_URL}${docsSlug}`,
        "_blank",
        "noopener,noreferrer",
      );
    }
  };

  // Retry loading the iframe
  const handleRetry = () => {
    setIsLoading(true);
    setHasError(false);
    setIsNotFound(false);
    if (iframeRef.current && iframeSrc) {
      iframeRef.current.src = iframeSrc;
    }
  };

  // Get parent module docs slug (strip last segment)
  const parentSlug = docsSlug
    ? docsSlug.split("/").slice(0, -1).join("/") + "/overview"
    : null;

  // Determine positioning classes based on language
  const drawerPositionClasses = isRTL
    ? "left-0 translate-x-[-100%] data-[open=true]:translate-x-0"
    : "right-0 translate-x-full data-[open=true]:translate-x-0";

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          "fixed inset-0 z-[60] bg-black/40 transition-opacity duration-300",
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none",
        )}
        onClick={closeDocs}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <div
        data-open={isOpen}
        className={cn(
          "fixed top-0 z-[61] h-full w-full max-w-[520px] bg-white shadow-2xl",
          "transform transition-transform duration-300 ease-in-out",
          "flex flex-col",
          drawerPositionClasses,
        )}
        role="dialog"
        aria-modal="true"
        aria-label={t("drawer.ariaLabel")}
      >
        {/* Header */}
        <div className={cn(
          "flex items-center justify-between border-b border-gray-200 px-4 py-3 flex-shrink-0",
          isRTL && "flex-row-reverse"
        )}>
          <div className={cn(
            "flex items-center gap-2 min-w-0",
            isRTL && "flex-row-reverse"
          )}>
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-blue-50">
              <svg
                className="h-4 w-4 text-blue-600"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
              </svg>
            </div>
            <h2 className="text-sm font-semibold text-gray-900 truncate">
              {currentTitle}
            </h2>
          </div>
          <div className={cn(
            "flex items-center gap-1 flex-shrink-0",
            isRTL && "flex-row-reverse"
          )}>
            <button
              onClick={openInNewTab}
              className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
              title={t("drawer.openInNewTab")}
            >
              <ExternalLink className="h-4 w-4" />
            </button>
            <button
              onClick={closeDocs}
              className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
              title={t("close")}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 relative overflow-hidden">
          {/* Loading indicator */}
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                <span className="text-sm text-gray-500">
                  {t("drawer.loading")}
                </span>
              </div>
            </div>
          )}

          {/* iframe */}
          {isOpen && iframeSrc && !hasError && !isNotFound && (
            <iframe
              ref={iframeRef}
              src={iframeSrc}
              title={t("drawer.iframeTitle")}
              className="h-full w-full border-0"
              // `allow-scripts allow-same-origin` together defeats the
              // sandbox — code inside the iframe can rewrite its own sandbox
              // attribute via window.top to escape. The docs site
              // (docs.botbat.io) is a different origin from the console, so
              // `allow-same-origin` doesn't grant the iframe access to its
              // own origin's normal capabilities here — and dropping it is
              // strictly safer. Scripts, popups (open-in-new-tab links), and
              // forms (search) are all the docs site needs.
              sandbox="allow-scripts allow-popups allow-forms"
              onLoad={() => {
                // Fallback: if postMessage doesn't fire, clear loading after timeout
                setTimeout(() => setIsLoading(false), 3000);
              }}
            />
          )}

          {/* Not found state */}
          {isOpen && isNotFound && (
            <div className="flex flex-col items-center justify-center h-full gap-4 text-gray-500 px-6">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-50">
                <svg
                  className="h-7 w-7 text-amber-500"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.3-4.3" />
                  <path d="M8 11h6" />
                </svg>
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-gray-700">
                  {t("drawer.notFound.title")}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {t("drawer.notFound.description")}
                </p>
              </div>
              <div className="flex flex-col items-center gap-2">
                {parentSlug && (
                  <button
                    onClick={() => {
                      setIsNotFound(false);
                      setIsLoading(true);
                      if (iframeRef.current) {
                        iframeRef.current.src = `${DOCS_BASE_URL}${parentSlug}?embed=1`;
                      }
                    }}
                    className="text-sm text-blue-600 hover:text-blue-700 underline"
                  >
                    {t("drawer.notFound.viewOverview")}
                  </button>
                )}
                <button
                  onClick={() =>
                    window.open(DOCS_BASE_URL, "_blank", "noopener,noreferrer")
                  }
                  className="text-sm text-gray-500 hover:text-gray-700 underline"
                >
                  {t("drawer.notFound.browseAll")}
                </button>
              </div>
            </div>
          )}

          {/* Error / timeout state */}
          {isOpen && hasError && !isNotFound && (
            <div className="flex flex-col items-center justify-center h-full gap-4 text-gray-500 px-6">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
                <svg
                  className="h-7 w-7 text-red-400"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="m15 9-6 6" />
                  <path d="m9 9 6 6" />
                </svg>
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-gray-700">
                  {t("drawer.error.title")}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {t("drawer.error.description")}
                </p>
              </div>
              <div className="flex flex-col items-center gap-2">
                <button
                  onClick={handleRetry}
                  className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
                >
                  {t("retry")}
                </button>
                <button
                  onClick={() =>
                    window.open(
                      docsSlug ? `${DOCS_BASE_URL}${docsSlug}` : DOCS_BASE_URL,
                      "_blank",
                      "noopener,noreferrer",
                    )
                  }
                  className="text-sm text-gray-500 hover:text-gray-700 underline"
                >
                  {t("drawer.error.openInNewTab")}
                </button>
              </div>
            </div>
          )}

          {/* No docs slug available */}
          {isOpen && !iframeSrc && !hasError && !isNotFound && (
            <div className="flex flex-col items-center justify-center h-full gap-4 text-gray-500 px-6">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gray-100">
                <svg
                  className="h-7 w-7 text-gray-400"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                </svg>
              </div>
              <p className="text-sm text-center">
                {t("drawer.noDocs.message")}
              </p>
              <button
                onClick={() =>
                  window.open(DOCS_BASE_URL, "_blank", "noopener,noreferrer")
                }
                className="text-sm text-gray-500 hover:text-gray-700 underline"
              >
                {t("drawer.noDocs.browseAll")}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default DocsDrawer;